/**
 * Recent admin actions tile — last 10 entries from core.admin_audit_log.
 *
 * Backend shape (buildRecentAdminActionsTile):
 *   { entries: Array<{ id, action, entity_type, entity_id, details, created_at }> }
 *
 * Gated on `audit.read_all` (backend pre-filters; this component renders
 * only when the tile is in the payload).
 */
import { Link } from 'react-router-dom';
import { formatDate } from '@/lib/utils';

interface AuditEntry {
  id: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

export function RecentActionsTile({ data }: { data: { entries: AuditEntry[] } }) {
  const entries = data?.entries ?? [];
  return (
    <div className="bg-card border rounded-lg p-4 md:col-span-2 lg:col-span-3">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">
          Recent admin actions
        </div>
        <Link to="/audit" className="text-xs text-primary">View all →</Link>
      </div>
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No admin actions yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted-foreground border-b">
              <th className="py-1 pr-3 font-medium">When</th>
              <th className="py-1 pr-3 font-medium">Action</th>
              <th className="py-1 pr-3 font-medium">Entity</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-b last:border-0">
                <td className="py-1 pr-3 text-xs text-muted-foreground whitespace-nowrap">
                  {formatDate(e.created_at)}
                </td>
                <td className="py-1 pr-3 font-mono text-xs">{e.action}</td>
                <td className="py-1 pr-3 text-xs">
                  {e.entity_type ?? '—'}
                  {e.entity_id ? (
                    <span className="text-muted-foreground"> {e.entity_id.slice(0, 8)}…</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
